import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { I18nService } from '../../../core/i18n/i18n.service';
import { BrandingSettingsTabComponent } from '../components/branding-settings-tab.component';
import { ClinicSettingsPanelComponent } from '../components/clinic-settings-panel.component';
import { GlobalSettingsTabComponent } from '../components/global-settings-tab.component';
import { MailSettingsTabComponent } from '../components/mail-settings-tab.component';
import { OperationsSettingsStateService } from '../components/operations-settings-state.service';
import { OperationsSettingsTabComponent } from '../components/operations-settings-tab.component';
import { RatingSettingsTabComponent } from '../components/rating-settings-tab.component';
import { AuthService } from '../../../core/auth/auth.service';
import { UserRole } from '../../../core/auth/user-role.type';
import {
  PageContextGuardComponent,
  PageShellComponent,
  TabStripComponent,
  TabStripItem,
} from '@clinic-platform/ui-components';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';

@Component({
  selector: 'app-settings-page',
  standalone: true,
  providers: [OperationsSettingsStateService],
  imports: [
    PageHeaderComponent,
    PageContextGuardComponent,
    PageShellComponent,
    TabStripComponent,
    CompanyContextRequiredComponent,
    BrandingSettingsTabComponent,
    ClinicSettingsPanelComponent,
    GlobalSettingsTabComponent,
    MailSettingsTabComponent,
    OperationsSettingsTabComponent,
    RatingSettingsTabComponent,
  ],
  template: `
    <lib-page-shell>
      <app-page-header
        [title]="pageTitle"
        [subtitle]="pageSubtitle"
      />

      @if (!isClinicsMode) {
        <lib-tab-strip
          [items]="settingsTabs"
          [activeId]="activeSettingsTab"
          (activeIdChange)="selectSettingsTab($event)"
        />
      }

      @if (isClinicsMode) {
        <app-clinic-settings-panel
          [companyId]="getTargetCompanyId()"
          [hasCompanyContext]="hasRequiredCompanyContext()"
        />
      }

      @if (!isClinicsMode && activeSettingsTab === 'operations' && systemAdminSettingsContextReason; as contextReason) {
        <lib-page-context-guard [blocked]="true">
          <app-company-context-required [message]="contextReason" />
        </lib-page-context-guard>
      } @else if (!isClinicsMode && activeSettingsTab === 'operations') {
        <app-operations-settings-tab
          [companyId]="getTargetCompanyId()"
          [contextText]="operationsContextText"
        />
      }

      @if (!isClinicsMode && canManageAdminSettings && activeSettingsTab === 'rating' && systemAdminSettingsContextReason; as contextReason) {
        <lib-page-context-guard [blocked]="true">
          <app-company-context-required [message]="contextReason" />
        </lib-page-context-guard>
      } @else if (!isClinicsMode && canManageAdminSettings && activeSettingsTab === 'rating') {
        <app-rating-settings-tab
          [companyId]="getTargetCompanyId()"
          [contextText]="ratingContextText"
        />
      }

      @if (!isClinicsMode && activeSettingsTab === 'branding' && systemAdminSettingsContextReason; as contextReason) {
        <lib-page-context-guard [blocked]="true">
          <app-company-context-required [message]="contextReason" />
        </lib-page-context-guard>
      } @else if (!isClinicsMode && activeSettingsTab === 'branding') {
        <app-branding-settings-tab
          [companyId]="getTargetCompanyId()"
          [contextText]="brandingContextText"
        />
      }

      @if (!isClinicsMode && canManageAdminSettings && activeSettingsTab === 'mail' && systemAdminSettingsContextReason; as contextReason) {
        <lib-page-context-guard [blocked]="true">
          <app-company-context-required [message]="contextReason" />
        </lib-page-context-guard>
      } @else if (!isClinicsMode && canManageAdminSettings && activeSettingsTab === 'mail') {
        <app-mail-settings-tab
          [companyId]="getTargetCompanyId()"
          [contextText]="mailContextText"
        />
      }

      @if (!isClinicsMode && canManageGlobalSettings && activeSettingsTab === 'global') {
        <app-global-settings-tab />
      }
    </lib-page-shell>
  `,
})
export class SettingsPageComponent implements OnInit, OnDestroy
{
  private static readonly COMPANY_CONTEXT_REQUIRED_ERROR_KEY = 'error.settingsCompanyContextRequired';

  private readonly authService = inject(AuthService);
  private readonly i18n = inject(I18nService);
  private readonly operationsState = inject(OperationsSettingsStateService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly subscriptions = new Subscription();
  private lastNonGlobalSettingsTab: 'operations' | 'rating' | 'mail' | 'branding' = 'operations';

  protected activeSettingsTab: 'operations' | 'rating' | 'mail' | 'branding' | 'global' = 'operations';
  protected settingsTabs: TabStripItem[] = [];

  protected get canManageAdminSettings(): boolean
  {
    const role = this.authService.getCurrentRole() as UserRole | null;
    return role === 'company_admin' || role === 'system_admin';
  }

  protected get canManageBrandingSettings(): boolean
  {
    const role = this.authService.getCurrentRole() as UserRole | null;
    return role === 'manager' || role === 'company_admin' || role === 'system_admin';
  }

  protected get isClinicsMode(): boolean
  {
    return (this.route.snapshot.data['settingsMode'] as string | undefined) === 'clinics';
  }

  protected get canManageGlobalSettings(): boolean
  {
    const role = this.authService.getCurrentRole() as UserRole | null;
    return role === 'system_admin' && this.systemAdminContext.isGlobalScope();
  }

  protected get pageTitle(): string
  {
    return this.isClinicsMode ? this.clinicLabelPlural : 'Settings';
  }

  protected get pageSubtitle(): string
  {
    return this.isClinicsMode
      ? `Manage ${this.clinicLabelPluralLower} and their working parameters`
      : 'Operational defaults and company overrides';
  }

  protected get clinicLabel(): string {
    return this.normalizeLabel(
      this.operationsState.form.controls.clinicLabel.value,
      OperationsSettingsStateService.DEFAULT_CLINIC_LABEL,
    );
  }

  protected get clinicLabelPlural(): string {
    return this.toPlural(this.clinicLabel);
  }

  protected get clinicLabelPluralLower(): string {
    return this.lowerFirst(this.clinicLabelPlural);
  }

  protected get operationsContextText(): string {
    const role = this.authService.getCurrentRole() as UserRole | null;
    if (role !== 'system_admin') {
      return 'Editing operations settings for your company. Values can override inherited platform defaults.';
    }

    return this.getTargetCompanyId()
      ? 'Editing operations overrides for the selected company.'
      : 'Editing global operations defaults used by new and inheriting companies.';
  }

  protected get mailContextText(): string {
    const role = this.authService.getCurrentRole() as UserRole | null;
    if (role !== 'system_admin') {
      return 'Editing mail settings for your company. These values override inherited platform defaults when saved.';
    }

    return this.getTargetCompanyId()
      ? 'Editing mail overrides for the selected company.'
      : 'Editing global mail defaults used by inheriting companies.';
  }

  protected get ratingContextText(): string {
    const role = this.authService.getCurrentRole() as UserRole | null;
    if (role !== 'system_admin') {
      return 'settings.rating.context.company';
    }

    return this.getTargetCompanyId()
      ? 'settings.rating.context.systemSelectedCompany'
      : 'settings.rating.context.systemSelectCompany';
  }

  protected get brandingContextText(): string {
    const role = this.authService.getCurrentRole() as UserRole | null;
    if (role !== 'system_admin') {
      return 'Editing tenant branding for your company. Changes affect auth surfaces and shared tenant shell.';
    }

    return this.getTargetCompanyId()
      ? 'Editing tenant branding for the selected company.'
      : 'Select a company to edit tenant branding.';
  }

  protected get systemAdminSettingsContextReason(): string | null {
    if (this.isClinicsMode) {
      return null;
    }

    const role = this.authService.getCurrentRole() as UserRole | null;
    if (role !== 'system_admin') {
      return null;
    }

    if (this.systemAdminContext.isGlobalScope()) {
      return null;
    }

    return this.getTargetCompanyId()
      ? null
      : SettingsPageComponent.COMPANY_CONTEXT_REQUIRED_ERROR_KEY;
  }

  protected getTargetCompanyId(): string | undefined
  {
    const role = this.authService.getCurrentRole() as UserRole | null;
    if (role !== 'system_admin') {
      return this.authService.getCurrentCompanyId() ?? undefined;
    }

    const routeCompanyId = this.route.snapshot.paramMap.get('companyId')?.trim();
    if (routeCompanyId) {
      return routeCompanyId;
    }

    if (this.systemAdminContext.isGlobalScope()) {
      return undefined;
    }

    const companyId = this.systemAdminContext.getCompanyId() || '';
    return companyId ? companyId : undefined;
  }

  public ngOnInit(): void
  {
    this.rebuildSettingsTabs();
    this.applyInitialSettingsTab();
    this.ensureActiveSettingsTab();
    if (this.isClinicsMode && this.hasRequiredCompanyContext()) {
      this.operationsState.load(this.getTargetCompanyId());
    }
    this.bindSystemAdminContextReload();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected hasRequiredCompanyContext(): boolean {
    const role = this.authService.getCurrentRole() as UserRole | null;
    if (role !== 'system_admin') {
      return true;
    }

    return !!this.getTargetCompanyId();
  }

  protected selectSettingsTab(tab: string): void {
    if (tab === 'operations' || tab === 'rating' || tab === 'mail' || tab === 'branding' || tab === 'global') {
      this.activeSettingsTab = tab;
      if (tab === 'operations' || tab === 'rating' || tab === 'mail' || tab === 'branding') {
        this.lastNonGlobalSettingsTab = tab;
        this.canonicalizeLegacyGlobalSettingsRoute();
      }
      this.syncSettingsTabQueryParam(tab);
    }
  }

  private bindSystemAdminContextReload(): void {
    const role = this.authService.getCurrentRole() as UserRole | null;
    if (role !== 'system_admin') {
      return;
    }

    this.subscriptions.add(
      this.systemAdminContext.refresh$.subscribe(() => {
        this.rebuildSettingsTabs();
        this.ensureActiveSettingsTab();
        if (this.isClinicsMode && this.hasRequiredCompanyContext()) {
          this.operationsState.load(this.getTargetCompanyId());
        }
      }),
    );
  }

  private applyInitialSettingsTab(): void
  {
    if (this.isClinicsMode) {
      return;
    }

    const queryTab = this.route.snapshot.queryParamMap.get('tab')?.trim();
    if (queryTab === 'operations' || queryTab === 'rating' || queryTab === 'mail' || queryTab === 'branding' || queryTab === 'global') {
      this.activeSettingsTab = queryTab;
      if (queryTab === 'operations' || queryTab === 'rating' || queryTab === 'mail' || queryTab === 'branding') {
        this.lastNonGlobalSettingsTab = queryTab;
      }
      return;
    }

    const initialTab = this.route.snapshot.data['initialSettingsTab'] as string | undefined;
    if (initialTab === 'operations' || initialTab === 'rating' || initialTab === 'mail' || initialTab === 'branding' || initialTab === 'global') {
      this.activeSettingsTab = initialTab;
      if (initialTab === 'operations' || initialTab === 'rating' || initialTab === 'mail' || initialTab === 'branding') {
        this.lastNonGlobalSettingsTab = initialTab;
      }
    }
  }

  private rebuildSettingsTabs(): void {
    const tabs: TabStripItem[] = [{ id: 'operations', label: this.i18n.translate('Operations') }];
    if (this.canManageAdminSettings) {
      tabs.push({ id: 'rating', label: this.i18n.translate('settings.rating.tab') });
      tabs.push({ id: 'mail', label: this.i18n.translate('Mail') });
    }
    if (this.canManageBrandingSettings) {
      tabs.push({ id: 'branding', label: this.i18n.translate('Branding') });
    }
    if (this.canManageGlobalSettings) {
      tabs.push({ id: 'global', label: this.i18n.translate('Global') });
    }
    this.settingsTabs = tabs;
  }

  private ensureActiveSettingsTab(): void {
    if (this.isClinicsMode) {
      return;
    }

    const availableTabs: Array<'operations' | 'rating' | 'mail' | 'branding' | 'global'> = ['operations'];
    if (this.canManageAdminSettings) {
      availableTabs.push('rating');
      availableTabs.push('mail');
    }
    if (this.canManageBrandingSettings) {
      availableTabs.push('branding');
    }
    if (this.canManageGlobalSettings) {
      availableTabs.push('global');
    }

    if (!availableTabs.includes(this.activeSettingsTab)) {
      if (availableTabs.includes(this.lastNonGlobalSettingsTab)) {
        this.activeSettingsTab = this.lastNonGlobalSettingsTab;
        this.syncSettingsTabQueryParam(this.activeSettingsTab);
        return;
      }

      this.activeSettingsTab = availableTabs[0];
      this.syncSettingsTabQueryParam(this.activeSettingsTab);
    }
  }

  private canonicalizeLegacyGlobalSettingsRoute(): void
  {
    const initialTab = this.route.snapshot.data['initialSettingsTab'] as string | undefined;
    if (initialTab !== 'global') {
      return;
    }

    const currentUrl = this.router.url.split('?')[0] ?? '';
    if (!currentUrl.endsWith('/global-settings')) {
      return;
    }

    const targetUrl = currentUrl.replace(/\/global-settings$/, '/settings');
    void this.router.navigateByUrl(targetUrl || '/settings', { replaceUrl: true });
  }

  private syncSettingsTabQueryParam(tab: 'operations' | 'rating' | 'mail' | 'branding' | 'global'): void
  {
    if (this.isClinicsMode) {
      return;
    }

    const currentTab = this.route.snapshot.queryParamMap.get('tab')?.trim() ?? '';
    if (currentTab === tab) {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private normalizeLabel(value: string | null | undefined, fallback: string): string {
    const normalized = (value ?? '').trim().replace(/s+/g, ' ');
    return normalized || fallback;
  }

  private lowerFirst(value: string): string {
    return value ? value.charAt(0).toLowerCase() + value.slice(1) : value;
  }

  private toPlural(value: string): string {
    if (!value) {
      return value;
    }

    const lower = value.toLowerCase();
    if (lower.endsWith('s')) {
      return value;
    }

    return `${value}s`;
  }
}
